/**
 * useWorkspaceMetrics Hook
 *
 * Custom React hook for fetching and managing workspace metrics state.
 * Handles analytics data, token history, loading states, and error handling.
 */

import { useState, useEffect, useCallback } from 'react';
import { WorkspaceAnalytics, TokenHistoryPoint, MetricsPeriod } from '../../../../../types/metrics';
import * as metricsService from '../../../../../services/workspaceMetricsService';

interface UseWorkspaceMetricsReturn {
  analytics: WorkspaceAnalytics | null;
  tokenHistory: TokenHistoryPoint[];
  isLoading: boolean;
  error: string | null;
  period: MetricsPeriod;
  setPeriod: (period: MetricsPeriod) => void;
  refetch: () => Promise<void>;
}

/**
 * Hook to fetch and manage workspace metrics data
 *
 * @param workspaceId - ID of the workspace to fetch metrics for (null to skip fetching)
 * @returns Object containing analytics data, loading state, error state, and control functions
 *
 * @example
 * ```tsx
 * const { analytics, tokenHistory, isLoading, error, period, setPeriod } = useWorkspaceMetrics(workspaceId);
 *
 * if (isLoading) return <LoadingSpinner />;
 * if (error) return <ErrorMessage message={error} />;
 * if (!analytics) return <EmptyState />;
 *
 * return <MetricsDisplay analytics={analytics} />;
 * ```
 */
export const useWorkspaceMetrics = (workspaceId: number | null): UseWorkspaceMetricsReturn => {
  const [analytics, setAnalytics] = useState<WorkspaceAnalytics | null>(null);
  const [tokenHistory, setTokenHistory] = useState<TokenHistoryPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<MetricsPeriod>(30);

  /**
   * Fetch metrics data from the API
   */
  const fetchMetrics = useCallback(async () => {
    // Don't fetch if no workspace is selected
    if (!workspaceId) {
      setAnalytics(null);
      setTokenHistory([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch both analytics and token history in parallel
      const [analyticsData, historyData] = await Promise.all([
        metricsService.getWorkspaceAnalytics(workspaceId, period),
        metricsService.getTokenHistory(workspaceId, period),
      ]);

      setAnalytics(analyticsData);
      setTokenHistory(historyData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao carregar métricas';
      setError(errorMessage);
      console.error('Error fetching workspace metrics:', err);
    } finally {
      setIsLoading(false);
    }
  }, [workspaceId, period]);

  /**
   * Auto-fetch when workspace or period changes
   */
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  return {
    analytics,
    tokenHistory,
    isLoading,
    error,
    period,
    setPeriod,
    refetch: fetchMetrics,
  };
};
